import type { Router } from 'vue-router';

/**
 * A tab (or an installed PWA) opened before a deploy still runs the previous build and asks for lazy chunks the
 * deploy removed. The dynamic import then fails and the navigation silently dies. Reload once onto the target
 * path, which fetches the new build; if the same target fails again right after that reload, it is a real error
 * and is left alone (no reload loop).
 */
const RELOADED_FOR = 'stale-chunk-reloaded-for';

function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  // Chrome: "Failed to fetch dynamically imported module", Firefox: "error loading dynamically imported module",
  // Safari: "Importing a module script failed". A plain API "Failed to fetch" must NOT match.
  return /dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(message);
}

function reloadOnce(path: string): void {
  try {
    if (sessionStorage.getItem(RELOADED_FOR) === path) return;
    sessionStorage.setItem(RELOADED_FOR, path);
  } catch {
    return; // no storage → no loop guard → do not risk a reload loop
  }
  window.location.assign(path);
}

export function installStaleChunkReload(router: Router): void {
  router.onError((error, to) => {
    // Offline, a chunk that does not load is the network, not a deploy: reloading would swap the open screen for the
    // browser's offline page (seen at e2e-screens, 31.10). Stay put; the offline banner already says why.
    if (isStaleChunkError(error) && navigator.onLine !== false) reloadOnce(to.fullPath);
  });
  router.afterEach(() => {
    try { sessionStorage.removeItem(RELOADED_FOR); } catch { /* nothing to clear */ }
  });
  // No 'vite:preloadError' listener on purpose: a failed preload rejects the lazy route's import and reaches
  // onError above, which knows the TARGET path. Reloading from that event lands on the current page instead.
}
